<template>
  <div>
    <v-card>
      <v-card-title>
        <span class="text-h5">{{ $t('global.adminBilling.notificationLog') }}</span>
        <v-spacer></v-spacer>
        <v-btn color="primary" small @click="refresh">
          <v-icon left>mdi-refresh</v-icon>
          {{ $t('global.refresh') }}
        </v-btn>
      </v-card-title>

      <v-card-text>
        <!-- Filter controls -->
        <v-row class="mb-4">
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.deviceId"
              :label="$t('global.adminBilling.filterByDeviceId')"
              clearable
              outlined
              dense
              type="number"
              @change="refresh"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-select
              v-model="filters.notificationType"
              :label="$t('global.adminBilling.notificationType')"
              :items="notificationTypes"
              clearable
              outlined
              dense
              @change="refresh"
            />
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field
              v-model="filters.limit"
              :label="$t('global.adminBilling.limit')"
              outlined
              dense
              type="number"
              @change="refresh"
            />
          </v-col>
        </v-row>

        <!-- Data table -->
        <v-data-table
          :headers="headers"
          :items="filteredLogs"
          :loading="loading"
          :items-per-page="25"
          class="elevation-1"
          :footer-props="{
            'items-per-page-options': [10, 25, 50, 100],
          }"
        >
          <!-- Notification Type Column -->
          <template v-slot:item.notificationType="{ item }">
            <v-chip :color="getNotificationTypeColor(item.notificationType)" dark small>
              {{ getNotificationTypeLabel(item.notificationType) }}
            </v-chip>
          </template>

          <!-- Days Before Expiry Column -->
          <template v-slot:item.daysBeforeExpiry="{ item }">
            <v-chip :color="getDaysColor(item.daysBeforeExpiry)" dark small>
              {{ formatDays(item.daysBeforeExpiry) }}
            </v-chip>
          </template>

          <!-- Device Info Column -->
          <template v-slot:item.device="{ item }">
            <div>
              <strong>{{ item.deviceName || item.deviceController }}</strong>
              <br />
              <small class="text--secondary">ID: {{ item.deviceId }}</small>
              <br />
              <small class="text--secondary">{{ item.companyName }}</small>
            </div>
          </template>

          <!-- User Info Column -->
          <template v-slot:item.user="{ item }">
            <div>
              <strong>{{ item.userEmail }}</strong>
              <br />
              <small class="text--secondary">
                {{ item.userFirstName }} {{ item.userLastName }}
              </small>
            </div>
          </template>

          <!-- Dates Column -->
          <template v-slot:item.dates="{ item }">
            <div>
              <small>
                <strong>{{ $t('global.adminBilling.sent') }}:</strong>
                {{ formatDate(item.sentAt) }}
              </small>
              <br />
              <small>
                <strong>{{ $t('global.adminBilling.expiry') }}:</strong>
                {{ formatDate(item.subscriptionExpiryDate) }}
              </small>
            </div>
          </template>

          <!-- Actions Column -->
          <template v-slot:item.actions="{ item }">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn
                  icon
                  small
                  color="primary"
                  v-on="on"
                  @click="resendNotification(item)"
                  :loading="resending[item.id]"
                >
                  <v-icon small>mdi-email-send</v-icon>
                </v-btn>
              </template>
              <span>{{ $t('global.adminBilling.resendNotification') }}</span>
            </v-tooltip>
          </template>
        </v-data-table>

        <!-- Summary Statistics -->
        <v-row class="mt-4">
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 primary--text">{{ statistics.total }}</div>
                <div class="text-caption">{{ $t('global.adminBilling.totalNotifications') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 info--text">{{ statistics.preExpiry }}</div>
                <div class="text-caption">
                  {{ $t('global.adminBilling.preExpiryNotifications') }}
                </div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 warning--text">{{ statistics.billingReminders }}</div>
                <div class="text-caption">{{ $t('global.adminBilling.billingReminders') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" md="3">
            <v-card outlined>
              <v-card-text class="text-center">
                <div class="text-h4 success--text">{{ statistics.uniqueDevices }}</div>
                <div class="text-caption">{{ $t('global.adminBilling.uniqueDevices') }}</div>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <!-- Send Notification Dialog -->
    <v-dialog v-model="sendDialog.show" max-width="600">
      <v-card>
        <v-card-title>
          {{ $t('global.adminBilling.sendNotification') }}
        </v-card-title>
        <v-card-text>
          <v-form ref="sendForm" v-model="sendDialog.valid">
            <v-select
              v-model="sendDialog.notificationType"
              :label="$t('global.adminBilling.notificationType')"
              :items="notificationTypes"
              :rules="[(v) => !!v || $t('global.required')]"
              outlined
            />

            <v-text-field
              v-model.number="sendDialog.deviceId"
              :label="$t('global.adminBilling.deviceId')"
              :rules="[(v) => !!v || $t('global.required')]"
              type="number"
              outlined
            />

            <v-select
              v-if="sendDialog.notificationType === 'PRE_EXPIRY'"
              v-model="sendDialog.daysBeforeExpiry"
              :label="$t('global.adminBilling.daysBeforeExpiry')"
              :items="[30, 14, 7, 3, 1]"
              :rules="[(v) => v !== null || $t('global.required')]"
              outlined
            />

            <v-select
              v-if="sendDialog.notificationType === 'BILLING_REMINDER'"
              v-model="sendDialog.reminderNumber"
              :label="$t('global.adminBilling.reminderNumber')"
              :items="[
                { text: '1 - 5 days', value: 1 },
                { text: '2 - 15 days', value: 2 },
                { text: '3 - 27 days', value: 3 },
                { text: '4 - 29 days (Final)', value: 4 },
              ]"
              :rules="[(v) => v !== null || $t('global.required')]"
              outlined
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="sendDialog.show = false">
            {{ $t('global.cancel') }}
          </v-btn>
          <v-btn
            color="primary"
            :loading="sendDialog.loading"
            :disabled="!sendDialog.valid"
            @click="sendNotification"
          >
            {{ $t('global.send') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { ADMIN_SUBSCRIPTION_NOTIFICATION_LOGS_QUERY } from '@/graphql/query/adminSubscriptionNotificationLogs'
import { ADMIN_SEND_SUBSCRIPTION_NOTIFICATION_MUTATION } from '@/graphql/mutations/adminSendSubscriptionNotification'

export default {
  name: 'SubscriptionNotificationLogTable',

  data() {
    return {
      loading: false,
      logs: [],
      resending: {},
      filters: {
        deviceId: null,
        notificationType: null,
        limit: 500,
      },
      notificationTypes: [
        { text: 'Pre-Expiry', value: 'PRE_EXPIRY' },
        { text: 'Billing Reminder', value: 'BILLING_REMINDER' },
      ],
      sendDialog: {
        show: false,
        valid: false,
        loading: false,
        deviceId: null,
        notificationType: 'PRE_EXPIRY',
        daysBeforeExpiry: null,
        reminderNumber: null,
      },
    }
  },

  computed: {
    headers() {
      return [
        {
          text: this.$t('global.adminBilling.notificationType'),
          value: 'notificationType',
          width: 150,
        },
        { text: this.$t('global.adminBilling.days'), value: 'daysBeforeExpiry', width: 100 },
        { text: this.$t('global.device'), value: 'device', width: 200 },
        { text: this.$t('global.user'), value: 'user', width: 250 },
        { text: this.$t('global.dates'), value: 'dates', width: 200 },
        { text: this.$t('global.actions'), value: 'actions', width: 100, sortable: false },
      ]
    },

    filteredLogs() {
      let result = [...this.logs]

      if (this.filters.notificationType) {
        result = result.filter((log) => log.notificationType === this.filters.notificationType)
      }

      return result
    },

    statistics() {
      return {
        total: this.logs.length,
        preExpiry: this.logs.filter((l) => l.notificationType === 'PRE_EXPIRY').length,
        billingReminders: this.logs.filter((l) => l.notificationType === 'BILLING_REMINDER').length,
        uniqueDevices: new Set(this.logs.map((l) => l.deviceId)).size,
      }
    },
  },

  mounted() {
    this.refresh()
  },

  methods: {
    async refresh() {
      this.loading = true
      try {
        const { data } = await this.$apollo.query({
          query: ADMIN_SUBSCRIPTION_NOTIFICATION_LOGS_QUERY,
          variables: {
            deviceId: this.filters.deviceId ? parseInt(this.filters.deviceId) : null,
            limit: parseInt(this.filters.limit) || 500,
          },
          fetchPolicy: 'network-only',
        })

        this.logs = data.adminSubscriptionNotificationLogs || []
      } catch (error) {
        console.error('Error fetching notification logs:', error)
        this.$toast.error(this.$t('global.adminBilling.errorLoadingLogs'))
      } finally {
        this.loading = false
      }
    },

    showSendDialog() {
      this.sendDialog = {
        show: true,
        valid: false,
        loading: false,
        deviceId: this.filters.deviceId,
        notificationType: 'PRE_EXPIRY',
        daysBeforeExpiry: null,
        reminderNumber: null,
      }
    },

    async sendNotification() {
      this.sendDialog.loading = true
      try {
        await this.$apollo.mutate({
          mutation: ADMIN_SEND_SUBSCRIPTION_NOTIFICATION_MUTATION,
          variables: {
            deviceId: parseInt(this.sendDialog.deviceId),
            notificationType: this.sendDialog.notificationType,
            daysBeforeExpiry: this.sendDialog.daysBeforeExpiry,
            reminderNumber: this.sendDialog.reminderNumber,
          },
        })

        this.$toast.success(this.$t('global.adminBilling.notificationSent'))
        this.sendDialog.show = false
        this.refresh()
      } catch (error) {
        console.error('Error sending notification:', error)
        this.$toast.error(this.$t('global.adminBilling.errorSendingNotification'))
      } finally {
        this.sendDialog.loading = false
      }
    },

    async resendNotification(item) {
      this.$set(this.resending, item.id, true)
      try {
        const variables = {
          deviceId: item.deviceId,
          notificationType: item.notificationType,
        }

        if (item.notificationType === 'PRE_EXPIRY') {
          variables.daysBeforeExpiry = Math.abs(item.daysBeforeExpiry)
        } else if (item.notificationType === 'BILLING_REMINDER') {
          // Map days to reminder number
          const daysToReminder = {
            5: 1,
            15: 2,
            27: 3,
            29: 4,
          }
          variables.reminderNumber = daysToReminder[Math.abs(item.daysBeforeExpiry)] || 1
        }

        await this.$apollo.mutate({
          mutation: ADMIN_SEND_SUBSCRIPTION_NOTIFICATION_MUTATION,
          variables,
        })

        this.$toast.success(this.$t('global.adminBilling.notificationResent'))
        this.refresh()
      } catch (error) {
        console.error('Error resending notification:', error)
        this.$toast.error(this.$t('global.adminBilling.errorResendingNotification'))
      } finally {
        this.$set(this.resending, item.id, false)
      }
    },

    getNotificationTypeColor(type) {
      return type === 'PRE_EXPIRY' ? 'info' : 'warning'
    },

    getNotificationTypeLabel(type) {
      return type === 'PRE_EXPIRY' ? 'Pre-Expiry' : 'Billing Reminder'
    },

    getDaysColor(days) {
      const absDays = Math.abs(days)
      if (days > 0) {
        // Pre-expiry
        if (absDays <= 3) return 'error'
        if (absDays <= 7) return 'warning'
        return 'info'
      } else {
        // Post-expiry (billing reminder)
        if (absDays >= 27) return 'error'
        return 'warning'
      }
    },

    formatDays(days) {
      if (days > 0) {
        return `${days} days before`
      } else if (days < 0) {
        return `${Math.abs(days)} days after`
      }
      return '0 days'
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleString()
    },
  },
}
</script>

<style scoped>
.v-chip {
  font-weight: 500;
}
</style>
